import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import useNotificationToast from './hooks/useNotificationToast';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import FloatingChat from './components/FloatingChat';
import Home from './pages/Home';
import VendorListing from './pages/VendorListing';
import VendorDetail from './pages/VendorDetail';
import Dashboard from './pages/Dashboard';
import Checklist from './pages/Checklist';
import GuestList from './pages/GuestList';
import BudgetTracker from './pages/BudgetTracker';
import WeddingWebsite from './pages/WeddingWebsite';
import Login from './pages/Login';
import Register from './pages/Register';
import VendorCategories from './pages/VendorCategories';
import AdminDashboard from './pages/AdminDashboard';
import VerifyVendors from './pages/VerifyVendors';
import VendorPortal from './pages/VendorPortal';
import Helpers from './pages/Helpers';
import NotificationSettings from './pages/NotificationSettings';
import AcceptInvite from './pages/AcceptInvite';
import VendorInviteAccept from './pages/VendorInviteAccept';
import VendorProjects from './pages/VendorProjects';
import EmployeeDashboard from './pages/EmployeeDashboard';
import WriteReview from './pages/WriteReview';
import MediaGallery from './pages/MediaGallery';
import PublicWedding from './pages/PublicWedding';
import VerifyEmail from './pages/VerifyEmail';
import ResetPassword from './pages/ResetPassword';
import MyInquiries from './pages/MyInquiries';
import MyBookings from './pages/MyBookings';
import BiddingPage from './pages/BiddingPage';
import SESMailbox from './pages/SESMailbox';
import EmailDashboard from './pages/EmailDashboard';
import Invitations from './pages/Invitations';
import RsvpPage from './pages/RsvpPage';
import WeddingVideos from './pages/WeddingVideos';
import AdminAnalytics from './pages/AdminAnalytics';
import AdminReleases from './pages/AdminReleases';
import StudioContracts from './pages/StudioContracts';
import ContractEditor from './pages/ContractEditor';
import ContractSign from './pages/ContractSign';
import NotFound from './pages/NotFound';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import AboutUs from './pages/AboutUs';
import { trackPageView } from './services/api';
import './App.css';

/** Mounts the global notification toast listener (needs Router + Auth context) */
function NotificationListener() {
  useNotificationToast();
  return null;
}

/**
 * Hides site chrome (nav, footer, chat) on the public contract-signing page.
 * The signer is a client, not a NepWed user — browse links and Sign Up would
 * only pull them away from signing.
 */
function Chrome({ children }) {
  const { pathname } = useLocation();
  return pathname.startsWith('/contract/') ? null : children;
}

/** Scroll to top on every route change */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

/** Track page views on route change */
function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname, document.referrer);
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <LanguageProvider>
    <AuthProvider>
      <Router>
        <NotificationListener />
        <PageViewTracker />
        <ScrollToTop />
        <ToastContainer
          position="top-right"
          autoClose={6000}
          newestOnTop
          closeOnClick={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          limit={4}
          theme="light"
        />
        <div className="App">
          <Chrome><Navbar /></Chrome>
          <main className="main-content">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/vendors" element={<VendorListing />} />
              <Route path="/vendors/:category" element={<VendorListing />} />
              <Route path="/vendor/:slug" element={<VendorDetail />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/checklist" element={<Checklist />} />
              <Route path="/guests" element={<GuestList />} />
              <Route path="/budget" element={<BudgetTracker />} />
              <Route path="/wedding-website" element={<WeddingWebsite />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/verify-email/:token" element={<VerifyEmail />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/verify-vendors" element={<VerifyVendors />} />
              <Route path="/email-dashboard" element={<EmailDashboard />} />
              <Route path="/vendor-portal" element={<VendorPortal />} />
              <Route path="/helpers" element={<Helpers />} />
              <Route path="/media" element={<MediaGallery />} />
              <Route path="/wedding-videos" element={<WeddingVideos />} />
              <Route path="/notifications" element={<NotificationSettings />} />
              <Route path="/invite/accept/:token" element={<AcceptInvite />} />
              <Route path="/vendor-invite/accept/:token" element={<VendorInviteAccept />} />
              <Route path="/vendor-projects" element={<VendorProjects />} />
              <Route path="/my-projects" element={<EmployeeDashboard />} />
              <Route path="/write-review" element={<WriteReview />} />
              <Route path="/w/:subdomain" element={<PublicWedding />} />
              <Route path="/my-inquiries" element={<MyInquiries />} />
              <Route path="/my-bookings" element={<MyBookings />} />
              <Route path="/invitations" element={<Invitations />} />
              <Route path="/rsvp/:token" element={<RsvpPage />} />
              <Route path="/bidding" element={<BiddingPage />} />
              <Route path="/admin/ses-mailbox" element={<SESMailbox />} />
              <Route path="/admin/analytics" element={<AdminAnalytics />} />
              <Route path="/admin/releases" element={<AdminReleases />} />
              <Route path="/admin/studio/contracts" element={<StudioContracts />} />
              <Route path="/admin/studio/contracts/:id" element={<ContractEditor />} />
              <Route path="/admin/studio/templates/:id" element={<ContractEditor mode="template" />} />
              <Route path="/contract/:token" element={<ContractSign />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          <Chrome><Footer /></Chrome>
          <Chrome><FloatingChat /></Chrome>
        </div>
      </Router>
    </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
